import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import { z } from 'zod'

const renameDraftSchema = z.object({
    name: z.string().min(1, 'Name is required').max(100),
})

interface RenameDraftDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    projectId: string
    draftId: string
    currentName: string
    onSuccess?: () => void
}

type FormData = z.infer<typeof renameDraftSchema>

export function RenameDraftDialog({
    open,
    onOpenChange,
    projectId,
    draftId,
    currentName,
    onSuccess,
}: RenameDraftDialogProps) {
    const queryClient = useQueryClient()

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<FormData>({
        resolver: zodResolver(renameDraftSchema),
        defaultValues: {
            name: currentName,
        },
    })

    // Update default values when currentName changes
    useEffect(() => {
        if (open) {
            reset({ name: currentName })
        }
    }, [currentName, open, reset])

    const { mutate: renameDraft, isPending } = useMutation({
        mutationFn: async (values: FormData) => {
            return api.patch(`/projects/${projectId}/drafts/${draftId}`, values)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['drafts', projectId] })
            onOpenChange(false)
            if (onSuccess) onSuccess()
        },
    })

    const onSubmit = (values: FormData) => {
        renameDraft(values)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Rename Draft</DialogTitle>
                    <DialogDescription>
                        Enter a new name for this draft.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">Name</Label>
                        <Input
                            id="name"
                            placeholder="Draft Name"
                            {...register('name')}
                        />
                        {errors.name && (
                            <p className="text-sm text-destructive">{errors.name.message}</p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isPending || isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isPending || isSubmitting}>
                            {(isPending || isSubmitting) && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Rename
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
